/**
 * MetricInfo — the ⓘ icon beside a metric label, with its definition and
 * reference ranges on hover or tap.
 *
 * Content comes from lib/metricInfo so one metric reads identically wherever
 * it appears. The trigger is a real <button> so the tooltip opens on tap and
 * on keyboard focus, not hover alone — half these pages are read on a phone.
 */

import { Info } from 'lucide-react';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from './ui/tooltip';
import { METRIC_INFO, TONE_CLASS } from '../lib/metricInfo';

export default function MetricInfo({ metric, side = 'top', className = '' }) {
  const info = METRIC_INFO[metric];
  if (!info) return null;

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`What is ${info.label}?`}
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex items-center justify-center text-muted-foreground/50
                        hover:text-foreground focus-visible:text-foreground focus:outline-none
                        transition-colors align-middle ${className}`}
          >
            <Info className="w-3 h-3" />
          </button>
        </TooltipTrigger>

        <TooltipContent side={side}>
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-foreground">{info.label}</div>
            <p className="text-muted-foreground">{info.what}</p>

            {info.ranges?.length > 0 && (
              <div className="flex flex-col gap-1 border-t border-border/60 pt-1.5">
                {info.ranges.map(({ band, verdict, tone }) => (
                  <div key={band} className="flex gap-2 items-baseline">
                    <span className={`font-mono text-[10px] shrink-0 w-[5.5rem] ${TONE_CLASS[tone] || ''}`}>
                      {band}
                    </span>
                    <span className="text-[11px] text-muted-foreground">{verdict}</span>
                  </div>
                ))}
              </div>
            )}

            {info.caveat && (
              <p className="text-[11px] text-amber-600/90 border-t border-border/60 pt-1.5">
                {info.caveat}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
